import ExcelJS from 'exceljs';
import {
  DayOfWeek,
  VenueAdmissionType,
  VenueType,
} from '../../../shared/enum/EnumRepository';

export const VENUE_BULK_TEMPLATE_FILE_NAME = 'venue_bulk_template.xlsx';
export const VENUE_BULK_TEMPLATE_MAX_ROWS = 1000;
export const VENUE_BULK_TEMPLATE_SHEET_NAME = 'Venues';
export const VENUE_OPENING_HOURS_TEMPLATE_SHEET_NAME = 'OpeningHours';
export const VENUE_BULK_TEMPLATE_GUIDE_SHEET_NAME = 'Guide';

export const VENUE_BULK_TEMPLATE_HEADERS = [
  'venueCode',
  'name',
  'nameEn',
  'type',
  'countryCode',
  'city',
  'district',
  'venueTag',
  'roadAddress',
  'roadAddressEn',
  'latitude',
  'longitude',
  'timeZone',
  'instagramUrl',
  'websiteUrl',
  'phoneNumber',
  'description',
  'descriptionEn',
  'admissionType',
  'admissionNote',
  'openingHoursNote',
  'naverMapUrl',
  'googleMapUrl',
] as const;

export const VENUE_OPENING_HOURS_TEMPLATE_HEADERS = [
  'venueCode',
  'dayOfWeek',
  'closed',
  'opensAt',
  'closesAt',
] as const;

const REQUIRED_VENUE_HEADERS = new Set<string>([
  'venueCode',
  'name',
  'type',
  'countryCode',
  'city',
  'roadAddress',
  'timeZone',
]);

const REQUIRED_OPENING_HOURS_HEADERS = new Set<string>([
  'venueCode',
  'dayOfWeek',
  'closed',
]);

const HEADER_FILL = 'FF27485D';
const REQUIRED_HEADER_FILL = 'FF173245';
const GUIDE_SECTION_FILL = 'FFDCEAF2';
const BORDER_COLOR = 'FFB9C6CE';

const VENUE_COLUMN_WIDTHS: Record<(typeof VENUE_BULK_TEMPLATE_HEADERS)[number], number> = {
  venueCode: 18,
  name: 24,
  nameEn: 24,
  type: 23,
  countryCode: 14,
  city: 18,
  district: 18,
  venueTag: 22,
  roadAddress: 34,
  roadAddressEn: 34,
  latitude: 14,
  longitude: 14,
  timeZone: 20,
  instagramUrl: 30,
  websiteUrl: 30,
  phoneNumber: 20,
  description: 36,
  descriptionEn: 36,
  admissionType: 18,
  admissionNote: 30,
  openingHoursNote: 30,
  naverMapUrl: 30,
  googleMapUrl: 30,
};

const OPENING_HOURS_COLUMN_WIDTHS: Record<
  (typeof VENUE_OPENING_HOURS_TEMPLATE_HEADERS)[number],
  number
> = {
  venueCode: 18,
  dayOfWeek: 16,
  closed: 12,
  opensAt: 14,
  closesAt: 14,
};

const listFormula = (values: string[]): string => `"${values.join(',')}"`;

const applyHeader = (
  sheet: ExcelJS.Worksheet,
  headers: readonly string[],
  requiredHeaders: Set<string>,
): void => {
  const row = sheet.addRow(headers);
  row.height = 30;
  row.eachCell({ includeEmpty: true }, (cell) => {
    const required = requiredHeaders.has(String(cell.value));
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: required ? REQUIRED_HEADER_FILL : HEADER_FILL },
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
    };
    cell.note = required ? 'Required' : 'Optional';
  });

  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };
};

const applyListValidation = (
  sheet: ExcelJS.Worksheet,
  header: string,
  values: string[],
  allowBlank: boolean,
): void => {
  const columnNumber = sheet.getRow(1).values instanceof Array
    ? (sheet.getRow(1).values as ExcelJS.CellValue[]).findIndex((value) => value === header)
    : -1;
  if (columnNumber < 1) return;

  for (let rowNumber = 2; rowNumber <= VENUE_BULK_TEMPLATE_MAX_ROWS + 1; rowNumber += 1) {
    sheet.getCell(rowNumber, columnNumber).dataValidation = {
      type: 'list',
      allowBlank,
      formulae: [listFormula(values)],
      showInputMessage: true,
      promptTitle: header,
      prompt: '목록에서 값을 선택하세요.',
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Invalid value',
      error: '목록에 있는 값만 입력할 수 있습니다.',
    };
  }
};

const configureDataSheet = (
  sheet: ExcelJS.Worksheet,
  headers: readonly string[],
  widths: Record<string, number>,
  textHeaders: readonly string[],
): void => {
  headers.forEach((header, index) => {
    const column = sheet.getColumn(index + 1);
    column.width = widths[header];
    column.alignment = { vertical: 'top', wrapText: true };
    if (textHeaders.includes(header)) column.numFmt = '@';
  });
};

const createVenuesSheet = (
  workbook: ExcelJS.Workbook,
  venueTagNames: string[],
): void => {
  const sheet = workbook.addWorksheet(VENUE_BULK_TEMPLATE_SHEET_NAME, {
    properties: { defaultRowHeight: 22 },
  });
  applyHeader(sheet, VENUE_BULK_TEMPLATE_HEADERS, REQUIRED_VENUE_HEADERS);
  configureDataSheet(
    sheet,
    VENUE_BULK_TEMPLATE_HEADERS,
    VENUE_COLUMN_WIDTHS,
    ['venueCode', 'countryCode', 'phoneNumber'],
  );
  applyListValidation(sheet, 'type', Object.values(VenueType), false);
  applyListValidation(sheet, 'admissionType', Object.values(VenueAdmissionType), true);
  if (venueTagNames.length > 0) {
    applyListValidation(sheet, 'venueTag', venueTagNames, true);
  }
};

const createOpeningHoursSheet = (workbook: ExcelJS.Workbook): void => {
  const sheet = workbook.addWorksheet(VENUE_OPENING_HOURS_TEMPLATE_SHEET_NAME, {
    properties: { defaultRowHeight: 22 },
  });
  applyHeader(
    sheet,
    VENUE_OPENING_HOURS_TEMPLATE_HEADERS,
    REQUIRED_OPENING_HOURS_HEADERS,
  );
  configureDataSheet(
    sheet,
    VENUE_OPENING_HOURS_TEMPLATE_HEADERS,
    OPENING_HOURS_COLUMN_WIDTHS,
    ['venueCode', 'opensAt', 'closesAt'],
  );
  applyListValidation(sheet, 'dayOfWeek', Object.values(DayOfWeek), false);
  applyListValidation(sheet, 'closed', ['TRUE', 'FALSE'], false);
};

const createGuideSheet = (workbook: ExcelJS.Workbook): void => {
  const sheet = workbook.addWorksheet(VENUE_BULK_TEMPLATE_GUIDE_SHEET_NAME, {
    properties: { defaultRowHeight: 22 },
  });
  sheet.columns = [
    { width: 24 },
    { width: 16 },
    { width: 78 },
  ];
  sheet.views = [{ state: 'frozen', ySplit: 2 }];

  sheet.mergeCells('A1:C1');
  const title = sheet.getCell('A1');
  title.value = 'Venue bulk upload guide';
  title.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: REQUIRED_HEADER_FILL } };
  title.alignment = { vertical: 'middle' };
  sheet.getRow(1).height = 34;

  const addSection = (name: string): void => {
    const row = sheet.addRow([name]);
    sheet.mergeCells(`A${row.number}:C${row.number}`);
    row.getCell(1).font = { bold: true, color: { argb: REQUIRED_HEADER_FILL } };
    row.getCell(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: GUIDE_SECTION_FILL },
    };
  };

  addSection('작성 방법');
  [
    ['시트/헤더', '', 'Venues와 OpeningHours 시트명 및 1행 헤더를 변경하지 마세요.'],
    ['행 수', '', `Venues 데이터는 최대 ${VENUE_BULK_TEMPLATE_MAX_ROWS.toLocaleString()}행입니다.`],
    ['식별자', '', 'venueCode, countryCode, phoneNumber는 텍스트로 입력하세요. 앞자리 0이 보존됩니다.'],
    ['좌표', '', 'latitude와 longitude는 둘 다 입력하거나 둘 다 비우세요. 위도 -90~90, 경도 -180~180, 소수점 6자리 이하입니다.'],
    ['운영시간', '', 'OpeningHours는 선택 사항입니다. venueCode로 Venues 행을 연결하며 한 공간에 요일별 1행, 최대 7행을 입력합니다.'],
    ['휴무', '', 'closed가 TRUE면 opensAt/closesAt을 비우고, FALSE면 HH:mm 형식으로 둘 다 입력하며 opensAt이 더 빨라야 합니다.'],
  ].forEach((values) => sheet.addRow(values));

  addSection('Venues 필드');
  const venueHeaderRow = sheet.addRow(['Field', 'Required', 'Description / allowed values']);
  const venueDescriptions: Record<string, string> = {
    venueCode: '고유 코드. 영문/숫자로 시작하며 영문, 숫자, _, - 사용',
    name: '공간명',
    nameEn: '영문 공간명',
    type: Object.values(VenueType).join(', '),
    countryCode: 'ISO 3166-1 alpha-2 (예: KR)',
    city: '도시',
    district: '구/군',
    venueTag: 'VenueTag 이름. 목록에서 선택',
    roadAddress: '도로명 주소',
    roadAddressEn: '영문 도로명 주소',
    latitude: '위도',
    longitude: '경도',
    timeZone: 'IANA time zone (예: Asia/Seoul)',
    instagramUrl: 'Instagram URL',
    websiteUrl: '웹사이트 URL',
    phoneNumber: '전화번호. 텍스트 형식 권장',
    description: '설명',
    descriptionEn: '영문 설명',
    admissionType: Object.values(VenueAdmissionType).join(', '),
    admissionNote: '입장 안내',
    openingHoursNote: '운영시간 안내',
    naverMapUrl: '네이버 지도 URL',
    googleMapUrl: '구글 지도 URL',
  };
  VENUE_BULK_TEMPLATE_HEADERS.forEach((header) => {
    sheet.addRow([
      header,
      REQUIRED_VENUE_HEADERS.has(header) ? 'YES' : '',
      venueDescriptions[header],
    ]);
  });

  addSection('OpeningHours 필드');
  const hoursHeaderRow = sheet.addRow(['Field', 'Required', 'Description / allowed values']);
  const openingHoursDescriptions: Record<string, string> = {
    venueCode: 'Venues 시트의 venueCode',
    dayOfWeek: Object.values(DayOfWeek).join(', '),
    closed: 'TRUE 또는 FALSE',
    opensAt: '영업 시작 시각 (HH:mm). closed=FALSE일 때 필수',
    closesAt: '영업 종료 시각 (HH:mm). closed=FALSE일 때 필수',
  };
  VENUE_OPENING_HOURS_TEMPLATE_HEADERS.forEach((header) => {
    sheet.addRow([
      header,
      REQUIRED_OPENING_HOURS_HEADERS.has(header) ? 'YES' : '',
      openingHoursDescriptions[header],
    ]);
  });

  [venueHeaderRow, hoursHeaderRow].forEach((row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
      cell.alignment = { vertical: 'middle' };
    });
  });
  sheet.eachRow((row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = { ...cell.alignment, vertical: 'top', wrapText: true };
    });
  });
};

export const buildVenueBulkTemplateWorkbook = (
  venueTagNames: string[] = [],
): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Architrave';
  workbook.title = 'Venue bulk upload template';
  workbook.subject = 'Venue bulk creation';

  createVenuesSheet(workbook, venueTagNames);
  createOpeningHoursSheet(workbook);
  createGuideSheet(workbook);
  return workbook;
};

export const buildVenueBulkTemplateBlob = async (
  venueTagNames: string[] = [],
): Promise<Blob> => {
  const workbook = buildVenueBulkTemplateWorkbook(venueTagNames);
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

export const downloadVenueBulkTemplate = async (
  venueTagNames: string[] = [],
): Promise<void> => {
  const blob = await buildVenueBulkTemplateBlob(venueTagNames);
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = VENUE_BULK_TEMPLATE_FILE_NAME;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};
